import cx from 'classnames'
import * as CSS from 'csstype'
import { useEffect, useLayoutEffect, useMemo, useRef, useState } from 'react'
import Styles from './ImageEditorComparison.module.scss'
import PostPreviewStyles from '../PostPreview.module.scss'
import { Text } from 'blaze-ui/Text/Text'
import { Button } from 'blaze-ui/Button/Button'
import { IconButton } from 'blaze-ui/IconButton/IconButton'
import { Edit1 } from 'blaze-ui/icons'
import { Spinner } from 'common/Spinner/Spinner'
import { ChevronLeft, ChevronRight } from 'blaze-ui/icons/24'
import { Preview } from 'components/PostPreviewModal/Preview'
import { ExternalIntegration } from 'api/clients/ExternalIntegrationsClient'
import { DesignChannel } from 'components/NewFileModal/Types'
import { ChannelType } from 'components/CampaignCreationModal/Types'
import { MediaObject } from 'types'
import { previewPostForComparisonMedia } from './previewPostForComparisonMedia'
import useResponsiveDesign from 'hooks/useResponsiveDesign'
import { calculateMaxWidth, getMinWidthForChannel, isStoryChannel, isVideoChannel } from '../utils'
import { providerFromChannelType } from 'components/ArticlePostPreview/ArticlePostPreviewContext'

const COMPARISON_PREVIEW_WINDOW_OFFSET = 280

interface ImageEditorComparisonProps {
  variantCount: number
  activeVariantIndex: number
  onActiveVariantIndexChange: (index: number) => void
  originalMediaObjects: MediaObject[]
  modifiedMediaObjects: MediaObject[]
  onUseOriginal: () => void
  onUseModified: () => void
  onEditMore: () => void
  isApplyingModified: boolean
  designKey: string
  integration?: ExternalIntegration
  channel: DesignChannel
  caption?: string
}

const getComparisonContainerClass = (ch: DesignChannel) => {
  switch (ch) {
    case DesignChannel.InstagramStory:
      return PostPreviewStyles.igStoryPreviewContainer
    case DesignChannel.FacebookStory:
      return PostPreviewStyles.fbStoryPreviewContainer
    case DesignChannel.InstagramReel:
      return PostPreviewStyles.igReelPreviewContainer
    case DesignChannel.TikTokPost:
      return PostPreviewStyles.tiktokPreviewContainer
    case DesignChannel.YoutubeShort:
      return PostPreviewStyles.youtubeShortPreviewContainer
    default:
      return undefined
  }
}

const ComparisonMediaPane = ({
  mediaObjects,
  designKey,
  integration,
  channel,
  previewWrapClassName,
  caption,
}: {
  mediaObjects: MediaObject[]
  designKey: string
  integration?: ExternalIntegration
  channel: DesignChannel
  previewWrapClassName?: string
  caption?: string
}) => {
  const { isMobile } = useResponsiveDesign()
  const [windowHeight, setWindowHeight] = useState(() => window.innerHeight)
  const [chromeHeight, setChromeHeight] = useState(0)
  const postPreviewHeaderRef = useRef<HTMLDivElement>(null)
  const postPreviewContentRef = useRef<HTMLDivElement>(null)
  const postPreviewFooterRef = useRef<HTMLDivElement>(null)

  useEffect(() => {
    const onResize = () => setWindowHeight(window.innerHeight)
    window.addEventListener('resize', onResize)
    if (window.visualViewport) window.visualViewport.addEventListener('resize', onResize)
    return () => {
      window.removeEventListener('resize', onResize)
      if (window.visualViewport) window.visualViewport.removeEventListener('resize', onResize)
    }
  }, [])

  const comparisonPost = useMemo(() => previewPostForComparisonMedia(mediaObjects, caption), [mediaObjects, caption])

  const firstMedia = mediaObjects[0]
  const aspectRatio =
    firstMedia?.width && firstMedia?.height && firstMedia.height > 0 ? firstMedia.width / firstMedia.height : 1

  const expandPreviewHorizontally = !!(firstMedia?.width && firstMedia?.height && firstMedia.width > firstMedia.height)

  const showVideoCrossPost = isVideoChannel(channel)
  const verticalSpace = windowHeight - COMPARISON_PREVIEW_WINDOW_OFFSET
  const initialWidthForChannel = Math.max(getMinWidthForChannel(channel), 280)

  const constrainedPortraitMaxWidth =
    !showVideoCrossPost && !isStoryChannel(channel)
      ? calculateMaxWidth(initialWidthForChannel, verticalSpace, aspectRatio, chromeHeight) ?? initialWidthForChannel
      : 550

  const postMaxWidth = expandPreviewHorizontally ? '630px' : `${constrainedPortraitMaxWidth}px`

  const isPortraitChannel = [
    ChannelType.InstagramPosts,
    DesignChannel.InstagramReel,
    DesignChannel.InstagramStory,
    DesignChannel.FacebookStory,
    DesignChannel.InstagramPost,
    DesignChannel.TikTokPost,
    DesignChannel.YoutubeShort,
  ].includes(channel as DesignChannel | ChannelType)

  const wrapperStyle = useMemo<CSS.Properties>(() => {
    if (isMobile) return {}
    return {
      ['--post-max-width' as string]: postMaxWidth,
      ['--rating-offset' as string]: '0px',
    }
  }, [isMobile, postMaxWidth])

  useLayoutEffect(() => {
    const h =
      (postPreviewHeaderRef.current?.clientHeight ?? 0) +
      (postPreviewContentRef.current?.clientHeight ?? 0) +
      (postPreviewFooterRef.current?.clientHeight ?? 0)
    setChromeHeight(h)
  }, [mediaObjects, channel, windowHeight, comparisonPost])

  const activePlatform = providerFromChannelType[channel]

  return (
    <div className={cx(Styles.previewWrap, previewWrapClassName)}>
      <div
        className={cx(
          PostPreviewStyles.preview,
          isPortraitChannel && PostPreviewStyles.isPortrait,
          Styles.comparisonPreviewFrame,
        )}
        style={wrapperStyle}
      >
        <div className={Styles.postPreviewMount}>
          <div className={Styles.shadow}>
            <Preview
              channel={channel}
              integration={integration}
              loading={false}
              post={comparisonPost}
              pollPreviewContentStream={false}
              hideConnectButton
              usernameContentSameLine
              maxWidth={630}
              containerClassName={cx(PostPreviewStyles.previewContainer, getComparisonContainerClass(channel))}
              photoBoxClassName={PostPreviewStyles.photoBox}
              almanacFileKey={designKey}
              isActive
              postPreviewHeaderRef={postPreviewHeaderRef}
              postPreviewContentRef={postPreviewContentRef}
              postPreviewFooterRef={postPreviewFooterRef}
              activePlatform={activePlatform}
            />
          </div>
        </div>
      </div>
    </div>
  )
}

export const ImageEditorComparison = ({
  variantCount,
  activeVariantIndex,
  onActiveVariantIndexChange,
  originalMediaObjects,
  modifiedMediaObjects,
  onUseOriginal,
  onUseModified,
  onEditMore,
  isApplyingModified,
  designKey,
  integration,
  channel,
  caption,
}: ImageEditorComparisonProps) => {
  const showVariantCarousel = variantCount > 1

  return (
    <div className={Styles.root}>
      <div className={Styles.columns}>
        <div className={Styles.column}>
          <div className={Styles.labelRow}>
            <Text variant="secondary" color="var(--dark-90)" className={Styles.comparisonColumnLabel}>
              Original
            </Text>
          </div>
          <ComparisonMediaPane
            mediaObjects={originalMediaObjects}
            designKey={designKey}
            integration={integration}
            channel={channel}
            caption={caption}
          />
          <div className={Styles.columnButtonContainer}>
            <Button
              variant="secondary"
              size="md"
              className={Styles.columnButton}
              isDisabled={isApplyingModified}
              onPress={onUseOriginal}
            >
              {isApplyingModified ? (
                <span className={Styles.loadingButtonContent}>
                  <Spinner className={Styles.loadingSpinner} />
                  Applying...
                </span>
              ) : (
                'Use Original'
              )}
            </Button>
          </div>
        </div>

        <div className={Styles.column}>
          <div className={Styles.modifiedHeader}>
            <div className={Styles.modifiedHeaderMain}>
              <div className={Styles.modifiedHeaderLeft}>
                <Text variant="secondary" color="var(--dark-90)" className={Styles.comparisonColumnLabel}>
                  Modified
                </Text>
              </div>
              {showVariantCarousel && (
                <div className={Styles.modifiedHeaderCenter}>
                  <div className={Styles.pagination}>
                    <IconButton
                      variant="ghost"
                      aria-label="Previous edit version"
                      onPress={() => onActiveVariantIndexChange(Math.max(0, activeVariantIndex - 1))}
                      icon={ChevronLeft}
                      isDisabled={activeVariantIndex <= 0}
                      color="var(--dark-80)"
                      size="xs"
                      className={Styles.variantPaginationIconButton}
                    />
                    <Text variant="metadata" color="var(--dark-90)" className={Styles.variantPaginationLabel}>
                      {activeVariantIndex + 1}/{variantCount}
                    </Text>
                    <IconButton
                      variant="ghost"
                      aria-label="Next edit version"
                      onPress={() => onActiveVariantIndexChange(Math.min(variantCount - 1, activeVariantIndex + 1))}
                      icon={ChevronRight}
                      isDisabled={activeVariantIndex >= variantCount - 1}
                      color="var(--dark-80)"
                      size="xs"
                      className={Styles.variantPaginationIconButton}
                    />
                  </div>
                </div>
              )}
            </div>
            <div className={Styles.modifiedHeaderRight}>
              <Button
                variant="secondary"
                size="xs"
                frontIcon={Edit1}
                className={Styles.editMoreButton}
                onPress={onEditMore}
                aria-label="Edit more"
              >
                Edit More
              </Button>
            </div>
          </div>
          <ComparisonMediaPane
            mediaObjects={modifiedMediaObjects}
            designKey={designKey}
            integration={integration}
            channel={channel}
            caption={caption}
          />
          <div className={Styles.columnButtonContainer}>
            <Button
              variant="primary"
              size="md"
              className={Styles.columnButton}
              isDisabled={isApplyingModified}
              onPress={onUseModified}
            >
              {isApplyingModified ? (
                <span className={Styles.loadingButtonContent}>
                  <Spinner className={Styles.loadingSpinner} />
                  Applying...
                </span>
              ) : (
                'Use Modified'
              )}
            </Button>
          </div>
        </div>
      </div>
    </div>
  )
}
